import { useState } from "react";
import { FileText, Check, X } from "lucide-react";

interface FileEdit {
  path: string;
  diff: string;
  applied: boolean;
  ts: number;
}

interface Props {
  edit: FileEdit;
  onApply: () => Promise<void>;
  onReject: () => void;
  onOpenPreview?: (path: string) => void;
}

function diffStats(diff: string): { added: number; removed: number } {
  let added = 0,
    removed = 0;
  for (const line of diff.split("\n")) {
    if (line.startsWith("+") && !line.startsWith("+++")) added++;
    else if (line.startsWith("-") && !line.startsWith("---")) removed++;
  }
  return { added, removed };
}

export function FileEditCard({ edit, onApply, onReject, onOpenPreview }: Props) {
  const { added, removed } = diffStats(edit.diff);
  const [busy, setBusy] = useState(false);

  async function handleApply() {
    setBusy(true);
    try {
      await onApply();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`file-edit-card ${edit.applied ? "is-applied" : ""}`}>
      <header className="file-edit-card__header">
        <FileText size={14} />
        <span className="file-edit-card__path">{edit.path}</span>
        <span className="file-edit-card__stats">
          <span className="stat-added">{added} +</span>
          <span className="stat-removed">{removed} -</span>
        </span>
        {edit.applied ? (
          <span className="file-edit-card__applied">
            <Check size={12} /> Applied
          </span>
        ) : (
          <span className="file-edit-card__actions">
            {edit.path.toLowerCase().endsWith(".html") && onOpenPreview && (
              <button
                type="button"
                onClick={() => onOpenPreview(edit.path)}
                aria-label="Open preview"
              >
                Open Preview
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleApply()}
              aria-label="Apply edit"
            >
              <Check size={12} /> Apply
            </button>
            <button type="button" onClick={onReject} aria-label="Reject edit">
              <X size={12} /> Reject
            </button>
          </span>
        )}
      </header>
      <pre className="file-edit-card__diff">
        <code>{edit.diff}</code>
      </pre>
    </div>
  );
}
